import React, { useState, useRef, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  FormItem,
  Input,
  DatePicker,
  NumberInput,
  Card,
  useToast,
  showConfirm,
  Tag,
  SearchableSelect,
  Select,
} from "../ui";
import type { Column } from "../ui";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  QrCodeIcon,
  MagnifyingGlassIcon,
  TableCellsIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { supabase, Rental, Dress, Customer, Branch } from "../config/supabase";
import {
  useRentals,
  useCreateRental,
  useUpdateRental,
  useDeleteRental,
  useReturnRental,
} from "../hooks/api/useRentals";
// Removed useDressesAll - now using SearchableSelect with useDressesForSelect
import { useCustomersAll } from "../hooks/api/useCustomers";
import { useCategoriesAll } from "../hooks/api/useCategories";
import {
  checkDressAvailability,
  AvailabilityResult,
} from "../utils/availability";
import CustomerSelect from "./CustomerSelect";
import RentalContract from "./RentalContract";
import { useBranchesAll } from "../hooks/api/useBranches";

const RentalManagement: React.FC = () => {
  // State management
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRental, setEditingRental] = useState<Rental | null>(null);
  const [selectedDress, setSelectedDress] = useState<Dress | null>(null);
  const [customerId, setCustomerId] = useState<string>("");
  const [rentalPrice, setRentalPrice] = useState<number>(0);
  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [rentalDate, setRentalDate] = useState<string>("");
  const [expectedReturnDate, setExpectedReturnDate] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [alterations, setAlterations] = useState<"CLIENT" | "DMU" | null>(null);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");

  // Availability validation
  const [availabilityResult, setAvailabilityResult] =
    useState<AvailabilityResult | null>(null);

  // Scanner states
  const [showScanner, setShowScanner] = useState(false);
  const [scannerStep, setScannerStep] = useState<"scan" | "form">("scan");
  const [scannerInput, setScannerInput] = useState("");
  const [scanningLoading, setScanningLoading] = useState(false);
  const scannerInputRef = useRef<HTMLInputElement>(null);

  // Contract states
  const [showContract, setShowContract] = useState(false);
  const [selectedRental, setSelectedRental] = useState<Rental | null>(null);

  // Calendar state - restored
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarRentals, setCalendarRentals] = useState<Rental[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  // Pagination, search, and sorting
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFromFilter, setDateFromFilter] = useState<string>("");
  const [dateToFilter, setDateToFilter] = useState<string>("");

  const { success, error: showError } = useToast();

  // React Query hooks - only for table view
  const {
    data: rentalsData,
    isLoading: loading,
    error: rentalsError,
  } = useRentals(
    currentPage,
    pageSize,
    viewMode === "table" ? searchTerm : "", // Only pass search term for table view
    "table",
    sortBy || undefined,
    sortOrder,
    // Add filter parameters
    categoryFilter === "all" ? undefined : categoryFilter,
    statusFilter === "all" ? undefined : statusFilter,
    dateFromFilter || undefined,
    dateToFilter || undefined
  );

  const { data: branchesAll = [] } = useBranchesAll();

  // Removed dresses state - now handled by SearchableSelect component
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: customers = [] } = useCustomersAll();
  const { data: categories = [] } = useCategoriesAll();

  const createRentalMutation = useCreateRental();
  const updateRentalMutation = useUpdateRental();
  const deleteRentalMutation = useDeleteRental();
  const returnRentalMutation = useReturnRental();

  // Extract data from hooks - only for table view
  const rentals =
    viewMode === "table" ? rentalsData?.rentals || [] : calendarRentals;
  const totalCount = rentalsData?.totalCount || 0;

  // Fetch calendar rentals with date range - restored original functionality
  const fetchCalendarRentals = useCallback(
    async (date: Date) => {
      if (viewMode !== "calendar") return;

      setCalendarLoading(true);
      try {
        const year = date.getFullYear();
        const month = date.getMonth();
        const startDate = new Date(year, month, 1).toISOString().split("T")[0];
        const endDate = new Date(year, month + 1, 0)
          .toISOString()
          .split("T")[0];

        const { data, error } = await supabase
          .from("rentals")
          .select(
            `
          *,
          dresses (
            id,
            name,
            barcode,
            rental_price,
            photo_url
          ),
          customers (
            id,
            name,
            email,
            phone
          )
        `
          )
          .gte("rental_date", startDate)
          .lte("rental_date", endDate)
          .order("rental_date", { ascending: false });

        if (error) throw error;
        setCalendarRentals(data || []);
      } catch (error: any) {
        showError("Error fetching calendar rentals: " + error.message);
      } finally {
        setCalendarLoading(false);
      }
    },
    [viewMode, showError]
  );

  // Handle mutation errors
  React.useEffect(() => {
    if (rentalsError) {
      showError("Error fetching rentals: " + rentalsError.message);
    }
  }, [rentalsError, showError]);

  // Fetch calendar data when view mode or date changes
  React.useEffect(() => {
    if (viewMode === "calendar") {
      fetchCalendarRentals(calendarDate);
    }
  }, [viewMode, calendarDate, fetchCalendarRentals]);

  // Handle search with debouncing
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Check availability when dress and dates are selected
  React.useEffect(() => {
    const checkAvailability = async () => {
      if (selectedDress && rentalDate && expectedReturnDate) {
        try {
          const availability = await checkDressAvailability(
            selectedDress.id,
            rentalDate,
            expectedReturnDate,
            editingRental?.id
          );
          setAvailabilityResult(availability);
        } catch (error: any) {
          console.error("Error checking availability:", error);
          setAvailabilityResult(null);
        }
      } else {
        setAvailabilityResult(null);
      }
    };

    checkAvailability();
  }, [selectedDress, rentalDate, expectedReturnDate, editingRental]);

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1); // Reset to first page when changing page size
    }
  };

  const handleScanDress = async (barcode: string) => {
    setScanningLoading(true);
    try {
      // Search for dress by barcode using Supabase
      const { data, error } = await supabase
        .from("dresses")
        .select(
          `
          id,
          name,
          barcode,
          rental_price,
          color,
          branch_id,
          brands (
            id,
            name
          ),
          branches (
            id,
            name
          )
        `
        )
        .eq("barcode", barcode)
        .eq("is_available", true)
        .single();

      if (error || !data) {
        showError("Dress not found with this barcode");
        return;
      }

      setSelectedDress(data as any);
      setRentalPrice(data.rental_price);
      if (data.branch_id) {
        setSelectedBranchId(data.branch_id);
      }
      setScannerStep("form");
      success("Dress found!");
    } catch (error: any) {
      showError("Error scanning dress: " + error.message);
    } finally {
      setScanningLoading(false);
    }
  };

  const handleSubmit = async (values: Record<string, any>) => {
    try {
      const submissionData = {
        dress_id: selectedDress?.id || values.dress_id,
        customer_id: customerId || values.customer_id,
        rental_date: rentalDate || values.rental_date,
        expected_return_date: expectedReturnDate || values.expected_return_date,
        rental_price: rentalPrice || values.rental_price,
        deposit_amount: depositAmount || values.deposit_amount,
        location: location || values.location,
        alterations: alterations || values.alterations,
        notes: values.notes || "",
      };

      // Check availability before creating/updating rental
      if (
        submissionData.dress_id &&
        submissionData.rental_date &&
        submissionData.expected_return_date
      ) {
        const availability = await checkDressAvailability(
          submissionData.dress_id,
          submissionData.rental_date,
          submissionData.expected_return_date,
          editingRental?.id // Exclude current rental if editing
        );

        if (!availability.isAvailable) {
          const conflictDetails = availability.conflictingRentals
            .map(
              (r) =>
                `${r.customer_name} (${new Date(
                  r.rental_date
                ).toLocaleDateString()} - ${new Date(
                  r.expected_return_date
                ).toLocaleDateString()})`
            )
            .join(", ");

          showError(
            `Dress is not available for the selected dates. Conflicts with: ${conflictDetails}`
          );
          setAvailabilityResult(availability);
          return;
        }
      }

      if (editingRental) {
        await updateRentalMutation.mutateAsync({
          id: editingRental.id,
          ...submissionData,
        });
        success("Rental updated successfully");
        handleModalClose();
      } else {
        const newRental = await createRentalMutation.mutateAsync({
          ...submissionData,
          status: "active",
        });
        success("Rental created successfully");
        handleModalClose();

        // Auto-open contract modal for new rentals
        setSelectedRental(newRental);
        setShowContract(true);
      }

      // Refresh calendar data if in calendar view
      if (viewMode === "calendar") {
        fetchCalendarRentals(calendarDate);
      }
    } catch (error: any) {
      showError("Error saving rental: " + error.message);
    }
  };

  const handleDelete = async (id: string, customerName: string) => {
    const confirmed = await showConfirm({
      title: `Delete rental for "${customerName}"?`,
      content: "This action cannot be undone.",
      onConfirm: () => {},
      type: "danger",
    });

    if (!confirmed) return;

    try {
      await deleteRentalMutation.mutateAsync(id);
      success("Rental deleted successfully");

      // Refresh calendar data if in calendar view
      if (viewMode === "calendar") {
        fetchCalendarRentals(calendarDate);
      }
    } catch (error: any) {
      showError("Error deleting rental: " + error.message);
    }
  };

  const handleReturn = async (
    id: string,
    customerName: string,
    dressName: string
  ) => {
    const confirmed = await showConfirm({
      title: `Mark dress as returned?`,
      content: `Confirm that "${dressName}" has been returned by ${customerName}.`,
      onConfirm: () => {},
    });

    if (!confirmed) return;

    try {
      await returnRentalMutation.mutateAsync(id);
      success("Dress marked as returned successfully");

      // Refresh calendar data if in calendar view
      if (viewMode === "calendar") {
        fetchCalendarRentals(calendarDate);
      }
    } catch (error: any) {
      showError("Error marking dress as returned: " + error.message);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setShowScanner(false);
    setEditingRental(null);
    setSelectedDress(null);
    setRentalPrice(0);
    setDepositAmount(0);
    setCustomerId("");
    setRentalDate("");
    setExpectedReturnDate("");
    setLocation("");
    setAlterations(null);
    setAvailabilityResult(null);
    setScannerStep("scan");
    setScannerInput("");
    setSelectedBranchId("");
  };

  const handleEdit = (rental: Rental) => {
    setEditingRental(rental);
    if (rental.dresses) {
      setSelectedDress(rental.dresses as any);
      setRentalPrice(rental.rental_price);
      if ((rental.dresses as any)?.branch_id) {
        setSelectedBranchId((rental.dresses as any).branch_id);
      }
    }
    setDepositAmount(rental.deposit_amount || 0);
    setCustomerId(rental.customer_id || "");
    setRentalDate(rental.rental_date.split("T")[0]);
    setExpectedReturnDate(rental.expected_return_date.split("T")[0]);
    setLocation(rental.location || "");
    setAlterations(rental.alterations || null);
    setModalVisible(true);
  };

  const handleQuickScan = () => {
    setEditingRental(null);
    setSelectedDress(null);
    setRentalPrice(0);
    setDepositAmount(0);
    setCustomerId("");
    setRentalDate("");
    setExpectedReturnDate("");
    setLocation("");
    setAlterations(null);
    setSelectedBranchId("");
    setScannerStep("scan");
    setShowScanner(true);
    setModalVisible(true);
  };

  const handleDressSelection = async (value: string | number, option: any) => {
    const dressId = String(value);

    if (!dressId) {
      setSelectedDress(null);
      setRentalPrice(0);
      return;
    }

    try {
      // Fetch the full dress details
      const { data, error } = await supabase
        .from("dresses")
        .select(
          `
          id,
          name,
          barcode,
          rental_price,
          color,
          branch_id,
          brands (
            id,
            name
          ),
          branches (
            id,
            name
          )
        `
        )
        .eq("id", dressId)
        .single();

      if (error || !data) {
        showError("Error loading dress details");
        return;
      }

      setSelectedDress(data as any);
      setRentalPrice(data.rental_price);
    } catch (error: any) {
      showError("Error loading dress details: " + error.message);
    }
  };

  const handleCustomerAdded = (newCustomer: Customer) => {
    setCustomerId(newCustomer.id);
  };

  const handleShowContract = (rental: Rental) => {
    setSelectedRental(rental);
    setShowContract(true);
  };

  const handleCloseContract = () => {
    setShowContract(false);
    setSelectedRental(null);
  };

  const handleSaveSignatures = (customerSignature: string | null, shopSignature: string | null) => {
    if (!selectedRental) return;
    updateRentalMutation.mutate({
      id: selectedRental.id,
      customer_signature: customerSignature,
      shop_signature: shopSignature,
    });
  };

  // Handle sorting
  const handleSort = (key: string, direction: "asc" | "desc") => {
    setSortBy(key);
    setSortOrder(direction);
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: string) => {
    setCurrentPage(1); // Reset to first page when filtering
    switch (filterType) {
      case "category":
        setCategoryFilter(value);
        break;
      case "status":
        setStatusFilter(value);
        break;
      case "dateFrom":
        setDateFromFilter(value);
        break;
      case "dateTo":
        setDateToFilter(value);
        break;
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setCategoryFilter("all");
    setStatusFilter("all");
    setDateFromFilter("");
    setDateToFilter("");
    setCurrentPage(1);
  };

  // Utility function to determine text color based on background color
  const getContrastColor = (hexColor: string): string => {
    // Remove # if present
    const color = hexColor.replace("#", "");

    // Convert to RGB
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white for dark colors, black for light colors
    return luminance > 0.5 ? "#000000" : "#ffffff";
  };

  // Table columns
  const columns: Column<Rental>[] = [
    {
      title: "Customer",
      dataIndex: "customers",
      key: "customer",
      sorter: true,
      render: (customer: any) => (
        <div>
          <div className="font-medium">{customer?.name || "N/A"}</div>
          <div className="text-sm text-gray-500">{customer?.email || ""}</div>
        </div>
      ),
    },
    {
      title: "Dress",
      dataIndex: "dresses",
      key: "dress",
      sorter: true,
      render: (dress: any) => (
        <div>
          <div className="font-medium">{dress?.name || "N/A"}</div>
          <div className="text-sm text-gray-500">{dress?.barcode || ""}</div>
        </div>
      ),
    },
    {
      title: "Category",
      dataIndex: "dresses",
      key: "category",
      sorter: true,
      render: (dress: any) => {
        const category = dress?.categories;
        if (!category) {
          return <Tag color="gray">No Category</Tag>;
        }
        const textColor = getContrastColor(category.color);
        return (
          <Tag
            style={{
              backgroundColor: category.color,
              color: textColor,
              border: `1px solid ${category.color}`,
              fontWeight: "500",
            }}
          >
            {category.name}
          </Tag>
        );
      },
    },
    {
      title: "Rental Date",
      dataIndex: "rental_date",
      key: "rental_date",
      sorter: true,
      render: (date: string) =>
        new Date(date).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          weekday: "short",
        }),
    },
    {
      title: "Return Date",
      dataIndex: "expected_return_date",
      key: "return_date",
      sorter: true,
      render: (date: string) =>
        new Date(date).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
          weekday: "short",
        }),
    },
    {
      title: "Price",
      dataIndex: "rental_price",
      key: "price",
      sorter: true,
      render: (price: number) => `$${price?.toFixed(2) || "0.00"}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      render: (status: string) => (
        <Tag
          color={
            status === "active"
              ? "blue"
              : status === "returned"
              ? "green"
              : "gray"
          }
        >
          {status?.toUpperCase() || "N/A"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: Rental) => (
        <div className="flex space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleShowContract(record)}
            className="text-purple-600 hover:text-purple-800"
            title="Generate rental contract"
          >
            <DocumentTextIcon className="h-4 w-4" />
          </Button>
          {record.status === "active" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                handleReturn(
                  record.id,
                  (record.customers as any)?.name || "Customer",
                  (record.dresses as any)?.name || "Dress"
                )
              }
              className="text-green-600 hover:text-green-800"
              title="Mark as returned"
            >
              <CheckCircleIcon className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(record)}
            className="text-blue-600 hover:text-blue-800"
            title="Edit rental"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              handleDelete(
                record.id,
                (record.customers as any)?.name || "Customer"
              )
            }
            className="text-red-600 hover:text-red-800"
            title="Delete rental"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const renderTableView = () => (
    <Table
      columns={columns}
      dataSource={rentals}
      loading={loading}
      pagination={{
        current: currentPage,
        pageSize: pageSize,
        total: totalCount,
        onChange: handlePageChange,
        showSizeChanger: true,
      }}
      onSort={handleSort}
      sortConfig={sortBy ? { key: sortBy, direction: sortOrder } : null}
    />
  );

  const renderCalendarView = () => {
    const generateCalendar = () => {
      const year = calendarDate.getFullYear();
      const month = calendarDate.getMonth();
      const firstDayOfMonth = new Date(year, month, 1);
      const lastDayOfMonth = new Date(year, month + 1, 0);
      const firstDayOfWeek = firstDayOfMonth.getDay();
      const daysInMonth = lastDayOfMonth.getDate();

      const days = [];

      // Add empty cells for days before the first day of the month
      for (let i = 0; i < firstDayOfWeek; i++) {
        days.push(null);
      }

      // Add days of the month
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
          day
        ).padStart(2, "0")}`;
        days.push(dateStr);
      }

      return days;
    };

    const getRentalsForDate = (dateStr: string) => {
      const filterBySearch = (rental: any) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        const customerName =
          (rental.customers as any)?.name?.toLowerCase() || "";
        const dressName = (rental.dresses as any)?.name?.toLowerCase() || "";
        return customerName.includes(term) || dressName.includes(term);
      };

      const rentalStarts = rentals.filter((rental) => {
        const rentalDate = rental.rental_date.split("T")[0];
        return dateStr === rentalDate && filterBySearch(rental);
      });

      const rentalReturns = rentals.filter((rental) => {
        const returnDate = rental.expected_return_date.split("T")[0];
        return dateStr === returnDate && filterBySearch(rental);
      });

      const activeRentals = rentals.filter((rental) => {
        const rentalDate = rental.rental_date.split("T")[0];
        const returnDate = rental.expected_return_date.split("T")[0];
        return (
          dateStr >= rentalDate &&
          dateStr <= returnDate &&
          dateStr !== rentalDate &&
          dateStr !== returnDate &&
          filterBySearch(rental)
        );
      });

      return { rentalStarts, rentalReturns, activeRentals };
    };

    const calendarDays = generateCalendar();

    // Check if search has any results
    const hasSearchResults = searchTerm
      ? rentals.some((rental) => {
          const term = searchTerm.toLowerCase();
          const customerName =
            (rental.customers as any)?.name?.toLowerCase() || "";
          const dressName = (rental.dresses as any)?.name?.toLowerCase() || "";
          return customerName.includes(term) || dressName.includes(term);
        })
      : true;

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-medium">
              {calendarDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </h3>
            {searchTerm && (
              <p className="text-sm text-gray-600 mt-1">
                Showing results for:{" "}
                <span className="font-medium">"{searchTerm}"</span>
              </p>
            )}
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCalendarDate(
                  new Date(
                    calendarDate.getFullYear(),
                    calendarDate.getMonth() - 1,
                    1
                  )
                )
              }
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCalendarDate(
                  new Date(
                    calendarDate.getFullYear(),
                    calendarDate.getMonth() + 1,
                    1
                  )
                )
              }
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Calendar Legend */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <span className="font-medium text-gray-700">Legend:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-100 border-l-2 border-green-500 rounded"></div>
              <span>📅 Rental Start</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-100 border-l-2 border-red-500 rounded"></div>
              <span>↩️ Return Due</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-100 rounded"></div>
              <span>🔵 Active Rental</span>
            </div>
          </div>
        </div>

        {/* Week headers */}
        <div className="grid grid-cols-7 gap-px mb-px">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="bg-gray-50 p-3 text-center font-medium text-gray-600 text-sm"
            >
              {day}
            </div>
          ))}
        </div>

        {calendarLoading ? (
          <div className="text-center text-gray-500 py-12">
            Loading calendar rentals...
          </div>
        ) : searchTerm && !hasSearchResults ? (
          <div className="text-center text-gray-500 py-12">
            <MagnifyingGlassIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium mb-2">No results found</h3>
            <p>
              No rentals match your search for "
              <span className="font-medium">{searchTerm}</span>"
            </p>
            <p className="text-sm mt-2">
              Try searching for a different customer name or dress name.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {calendarDays.map((dateStr, index) => {
              const dayRentals = dateStr
                ? getRentalsForDate(dateStr)
                : { rentalStarts: [], rentalReturns: [], activeRentals: [] };
              const dayNumber = dateStr ? new Date(dateStr).getDate() : null;
              const totalEvents =
                dayRentals.rentalStarts.length +
                dayRentals.rentalReturns.length +
                dayRentals.activeRentals.length;

              return (
                <div key={index} className="bg-white min-h-[120px] p-2">
                  {dayNumber && (
                    <>
                      <div className="font-medium text-sm text-gray-900 mb-2">
                        {dayNumber}
                      </div>
                      <div className="space-y-1">
                        {/* Rental Starts - Green */}
                        {dayRentals.rentalStarts
                          .slice(0, 2)
                          .map((rental: any) => (
                            <div
                              key={`start-${rental.id}`}
                              className="text-xs p-1 rounded truncate bg-green-100 text-green-800 border-l-2 border-green-500"
                              title={`RENTAL START: ${
                                (rental.customers as any)?.name
                              } - ${(rental.dresses as any)?.name}`}
                            >
                              📅{" "}
                              {(rental.customers as any)?.name?.split(" ")[0]} -{" "}
                              {(rental.dresses as any)?.name?.substring(0, 12)}
                              {(rental.dresses as any)?.name?.length > 12 &&
                                "..."}
                            </div>
                          ))}

                        {/* Rental Returns - Red */}
                        {dayRentals.rentalReturns
                          .slice(0, 2)
                          .map((rental: any) => (
                            <div
                              key={`return-${rental.id}`}
                              className="text-xs p-1 rounded truncate bg-red-100 text-red-800 border-l-2 border-red-500"
                              title={`RETURN DUE: ${
                                (rental.customers as any)?.name
                              } - ${(rental.dresses as any)?.name}`}
                            >
                              ↩️{" "}
                              {(rental.customers as any)?.name?.split(" ")[0]} -{" "}
                              {(rental.dresses as any)?.name?.substring(0, 12)}
                              {(rental.dresses as any)?.name?.length > 12 &&
                                "..."}
                            </div>
                          ))}

                        {/* Active Rentals - Blue */}
                        {dayRentals.activeRentals
                          .slice(0, 1)
                          .map((rental: any) => (
                            <div
                              key={`active-${rental.id}`}
                              className="text-xs p-1 rounded truncate bg-blue-100 text-blue-800"
                              title={`ACTIVE: ${
                                (rental.customers as any)?.name
                              } - ${(rental.dresses as any)?.name}`}
                            >
                              🔵{" "}
                              {(rental.customers as any)?.name?.split(" ")[0]} -{" "}
                              {(rental.dresses as any)?.name?.substring(0, 12)}
                              {(rental.dresses as any)?.name?.length > 12 &&
                                "..."}
                            </div>
                          ))}

                        {totalEvents > 3 && (
                          <div className="text-xs text-gray-500 p-1">
                            +{totalEvents - 3} more
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <CalendarIcon className="h-6 w-6 mr-2" />
          Rental Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage dress rentals, bookings, and availability tracking
        </p>
      </div>

      <div className="flex justify-between items-center">
        <div className="flex space-x-3">
          <Button
            variant="primary"
            size="lg"
            onClick={() => setModalVisible(true)}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-semibold px-6 py-3"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Rental
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleQuickScan}
            className="px-6 py-3"
          >
            <QrCodeIcon className="h-5 w-5 mr-2" />
            Quick Scan
          </Button>
        </div>
      </div>

      {/* View Mode Selector and Search */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex space-x-2">
            <Button
              variant={viewMode === "table" ? "primary" : "outline"}
              size="sm"
              onClick={() => setViewMode("table")}
            >
              <TableCellsIcon className="h-4 w-4 mr-2" />
              Table
            </Button>
            <Button
              variant={viewMode === "calendar" ? "primary" : "outline"}
              size="sm"
              onClick={() => setViewMode("calendar")}
            >
              <CalendarIcon className="h-4 w-4 mr-2" />
              Calendar
            </Button>
          </div>

          {(viewMode === "table" || viewMode === "calendar") && (
            <div className="flex-shrink-0">
              <Input
                placeholder={
                  viewMode === "table"
                    ? "Search by barcode, dress name, or customer..."
                    : "Search by customer name or dress name..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
                className="w-80"
              />
            </div>
          )}
        </div>

        {/* Filters - Only show for table view */}
        {viewMode === "table" && (
          <Card className="bg-gray-50">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-medium text-gray-700">
                Filter Rentals
              </h3>
              {(categoryFilter !== "all" ||
                statusFilter !== "all" ||
                dateFromFilter ||
                dateToFilter) && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                    {
                      [
                        categoryFilter !== "all" ? "category" : "",
                        statusFilter !== "all" ? "status" : "",
                        dateFromFilter ? "from date" : "",
                        dateToFilter ? "to date" : "",
                      ].filter(Boolean).length
                    }{" "}
                    filter(s) active
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear All
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Category
                </label>
                <Select
                  value={categoryFilter === "all" ? undefined : categoryFilter}
                  onChange={(value) =>
                    handleFilterChange("category", String(value) || "all")
                  }
                  options={categories.map((category: any) => ({
                    value: category.id,
                    label: category.name,
                  }))}
                  placeholder="Select category..."
                  showSearch={true}
                  allowClear={true}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Status
                </label>
                <Select
                  value={statusFilter === "all" ? undefined : statusFilter}
                  onChange={(value) =>
                    handleFilterChange("status", String(value) || "all")
                  }
                  options={[
                    { value: "active", label: "Active" },
                    { value: "returned", label: "Returned" },
                    { value: "overdue", label: "Overdue" },
                    { value: "cancelled", label: "Cancelled" },
                  ]}
                  placeholder="Select status..."
                  allowClear={true}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  From Date
                </label>
                <DatePicker
                  value={dateFromFilter}
                  onChange={(date) =>
                    handleFilterChange("dateFrom", date || "")
                  }
                  placeholder="Select from date..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  To Date
                </label>
                <DatePicker
                  value={dateToFilter}
                  onChange={(date) => handleFilterChange("dateTo", date || "")}
                  placeholder="Select to date..."
                />
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Content */}
      <Card>
        {viewMode === "table" && renderTableView()}
        {viewMode === "calendar" && renderCalendarView()}
      </Card>

      {/* Modal */}
      <Modal
        isOpen={modalVisible}
        onClose={handleModalClose}
        title={
          editingRental
            ? "Edit Rental"
            : showScanner
            ? "Quick Scan Rental"
            : "Add New Rental"
        }
        size="lg"
      >
        {showScanner ? (
          <div className="space-y-4">
            {scannerStep === "scan" ? (
              <div className="space-y-4">
                <div className="text-center">
                  <QrCodeIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium mb-2">
                    Scan Dress Barcode
                  </h3>
                  <p className="text-gray-600">
                    Scan or manually enter the dress barcode
                  </p>
                </div>

                <Input
                  ref={scannerInputRef}
                  placeholder="Scan or enter barcode..."
                  value={scannerInput}
                  onChange={(e) => setScannerInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && scannerInput.trim()) {
                      handleScanDress(scannerInput.trim());
                    }
                  }}
                  autoFocus
                />

                <div className="flex justify-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setScannerStep("form")}
                  >
                    Skip Scan
                  </Button>
                  <Button
                    onClick={() => handleScanDress(scannerInput.trim())}
                    disabled={!scannerInput.trim() || scanningLoading}
                    loading={scanningLoading}
                  >
                    Search
                  </Button>
                </div>
              </div>
            ) : (
              <Form
                onSubmit={handleSubmit}
                initialValues={
                  editingRental
                    ? {
                        dress_id: editingRental.dress_id,
                        customer_id: editingRental.customer_id,
                        rental_date: editingRental.rental_date,
                        expected_return_date:
                          editingRental.expected_return_date,
                        rental_price: editingRental.rental_price,
                        deposit_amount: editingRental.deposit_amount,
                        location: editingRental.location,
                        alterations: editingRental.alterations,
                        notes: editingRental.notes,
                      }
                    : {
                        dress_id: selectedDress?.id,
                        rental_price: rentalPrice,
                        deposit_amount: depositAmount,
                        customer_id: customerId,
                        location: location,
                        alterations: alterations,
                      }
                }
              >
                <div className="space-y-4">
                  <FormItem label="Branch" required>
                    <Select
                      placeholder="Select branch first..."
                      value={selectedBranchId || undefined}
                      onChange={(value) => {
                        const newBranchId = String(value);
                        if (newBranchId !== selectedBranchId) {
                          setSelectedBranchId(newBranchId);
                          setSelectedDress(null);
                          setRentalPrice(0);
                        }
                      }}
                      options={branchesAll.map((branch: Branch) => ({
                        value: branch.id,
                        label: branch.name,
                      }))}
                    />
                  </FormItem>

                  <FormItem label="Dress" required>
                    <SearchableSelect
                      placeholder={selectedBranchId ? "Search or select dress" : "Select a branch first"}
                      value={selectedDress?.id}
                      onChange={handleDressSelection}
                      searchPlaceholder="Search by dress name, barcode, or color..."
                      allowClear={true}
                      branchId={selectedBranchId || undefined}
                      disabled={!selectedBranchId}
                    />
                  </FormItem>

                  <FormItem label="Customer" required>
                    <CustomerSelect
                      value={customerId}
                      onChange={(id: string | number) =>
                        setCustomerId(String(id))
                      }
                      onCustomerAdded={handleCustomerAdded}
                    />
                  </FormItem>

                  <div className="grid grid-cols-2 gap-4">
                    <FormItem label="Rental Date" required>
                      <DatePicker
                        value={rentalDate}
                        onChange={(date) => setRentalDate(date || "")}
                        placeholder="Select rental date"
                      />
                    </FormItem>

                    <FormItem label="Expected Return Date" required>
                      <DatePicker
                        value={expectedReturnDate}
                        onChange={(date) => setExpectedReturnDate(date || "")}
                        placeholder="Select return date"
                      />
                    </FormItem>
                  </div>

                  {/* Availability Validation Display */}
                  {availabilityResult && !availabilityResult.isAvailable && (
                    <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <div className="bg-red-500 rounded-full p-1.5 flex-shrink-0">
                          <ExclamationTriangleIcon className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-red-900 text-sm mb-2">
                            ❌ Not Available
                          </h4>
                          <div className="space-y-2">
                            {availabilityResult.conflictingRentals.map(
                              (conflict, index) => (
                                <div
                                  key={index}
                                  className="bg-white border border-red-200 rounded p-2 text-xs"
                                >
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium text-red-900">
                                        👤 {conflict.customer_name}
                                      </p>
                                      <p className="text-red-700">
                                        📅{" "}
                                        {new Date(
                                          conflict.rental_date
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                        })}{" "}
                                        →{" "}
                                        {new Date(
                                          conflict.expected_return_date
                                        ).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                        })}
                                      </p>
                                    </div>
                                    <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                                      Booked
                                    </span>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                          {availabilityResult.nextAvailableDate && (
                            <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                              <p className="text-blue-900 text-sm">
                                💡{" "}
                                <span className="font-medium">
                                  Available from:
                                </span>{" "}
                                <span className="font-bold">
                                  {new Date(
                                    availabilityResult.nextAvailableDate
                                  ).toLocaleDateString("en-US", {
                                    weekday: "short",
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </span>
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {availabilityResult && availabilityResult.isAvailable && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-4">
                      <div className="flex items-center space-x-3">
                        <div className="bg-green-500 rounded-full p-1.5">
                          <CheckCircleIcon className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-green-900 text-sm">
                            ✅ Available!
                          </h4>
                          <p className="text-green-800 text-xs">
                            This dress is free for your selected dates
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <FormItem label="Rental Price" required>
                      <NumberInput
                        name="rental_price"
                        value={rentalPrice}
                        onChange={(value) => setRentalPrice(value || 0)}
                        min={0}
                        step={0.01}
                        prefix="$"
                      />
                    </FormItem>

                    <FormItem label="Deposit Amount" required>
                      <NumberInput
                        name="deposit_amount"
                        value={depositAmount}
                        onChange={(value) => setDepositAmount(value || 0)}
                        min={0}
                        step={0.01}
                        prefix="$"
                        placeholder="0.00"
                      />
                    </FormItem>
                  </div>

                  <FormItem label="Location (Event Location)" required>
                    <Input
                      name="location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Where is the dress being rented for?"
                    />
                  </FormItem>

                  <FormItem label="Dress Alterations">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="alterations"
                            value="CLIENT"
                            checked={alterations === "CLIENT"}
                            onChange={(e) =>
                              setAlterations(e.target.value as "CLIENT")
                            }
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            CLIENT
                          </span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="alterations"
                            value="DMU"
                            checked={alterations === "DMU"}
                            onChange={(e) =>
                              setAlterations(e.target.value as "DMU")
                            }
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm font-medium text-gray-700">
                            DMU
                          </span>
                        </label>
                      </div>
                    </div>
                  </FormItem>

                  <FormItem name="notes" label="Notes">
                    <Input placeholder="Additional notes..." />
                  </FormItem>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button variant="outline" onClick={handleModalClose}>
                    Cancel
                  </Button>
                  <Button type="submit" variant="primary">
                    {editingRental ? "Update Rental" : "Create Rental"}
                  </Button>
                </div>
              </Form>
            )}
          </div>
        ) : (
          <Form
            onSubmit={handleSubmit}
            initialValues={
              editingRental
                ? {
                    dress_id: editingRental.dress_id,
                    customer_id: editingRental.customer_id,
                    rental_date: editingRental.rental_date,
                    expected_return_date: editingRental.expected_return_date,
                    rental_price: editingRental.rental_price,
                    deposit_amount: editingRental.deposit_amount,
                    location: editingRental.location,
                    alterations: editingRental.alterations,
                    notes: editingRental.notes,
                  }
                : {
                    dress_id: selectedDress?.id,
                    rental_price: rentalPrice,
                    deposit_amount: depositAmount,
                    customer_id: customerId,
                    location: location,
                    alterations: alterations,
                  }
            }
          >
            <div className="space-y-4">
              <FormItem label="Branch" required>
                <Select
                  placeholder="Select branch first..."
                  value={selectedBranchId || undefined}
                  onChange={(value) => {
                    const newBranchId = String(value);
                    if (newBranchId !== selectedBranchId) {
                      setSelectedBranchId(newBranchId);
                      setSelectedDress(null);
                      setRentalPrice(0);
                    }
                  }}
                  options={branchesAll.map((branch: Branch) => ({
                    value: branch.id,
                    label: branch.name,
                  }))}
                />
              </FormItem>

              <FormItem label="Dress" required>
                <SearchableSelect
                  placeholder={selectedBranchId ? "Search or select dress" : "Select a branch first"}
                  value={selectedDress?.id}
                  onChange={handleDressSelection}
                  searchPlaceholder="Search by dress name, barcode, or color..."
                  allowClear={true}
                  branchId={selectedBranchId || undefined}
                  disabled={!selectedBranchId}
                />
              </FormItem>

              <FormItem label="Customer" required>
                <CustomerSelect
                  value={customerId}
                  onChange={(id: string | number) => setCustomerId(String(id))}
                  onCustomerAdded={handleCustomerAdded}
                />
              </FormItem>

              <div className="grid grid-cols-2 gap-4">
                <FormItem label="Rental Date" required>
                  <DatePicker
                    value={rentalDate}
                    onChange={(date) => setRentalDate(date || "")}
                    placeholder="Select rental date"
                  />
                </FormItem>

                <FormItem label="Expected Return Date" required>
                  <DatePicker
                    value={expectedReturnDate}
                    onChange={(date) => setExpectedReturnDate(date || "")}
                    placeholder="Select return date"
                  />
                </FormItem>
              </div>

              {/* Availability Validation Display */}
              {availabilityResult && !availabilityResult.isAvailable && (
                <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-red-500 rounded-full p-1.5 flex-shrink-0">
                      <ExclamationTriangleIcon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-red-900 text-sm mb-2">
                        ❌ Not Available
                      </h4>
                      <div className="space-y-2">
                        {availabilityResult.conflictingRentals.map(
                          (conflict, index) => (
                            <div
                              key={index}
                              className="bg-white border border-red-200 rounded p-2 text-xs"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-red-900">
                                    👤 {conflict.customer_name}
                                  </p>
                                  <p className="text-red-700">
                                    📅{" "}
                                    {new Date(
                                      conflict.rental_date
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })}{" "}
                                    →{" "}
                                    {new Date(
                                      conflict.expected_return_date
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </p>
                                </div>
                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                                  Booked
                                </span>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                      {availabilityResult.nextAvailableDate && (
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                          <p className="text-blue-900 text-sm">
                            💡{" "}
                            <span className="font-medium">Available from:</span>{" "}
                            <span className="font-bold">
                              {new Date(
                                availabilityResult.nextAvailableDate
                              ).toLocaleDateString("en-US", {
                                weekday: "short",
                                month: "short",
                                day: "numeric",
                              })}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {availabilityResult && availabilityResult.isAvailable && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-500 rounded-full p-1.5">
                      <CheckCircleIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-green-900 text-sm">
                        ✅ Available!
                      </h4>
                      <p className="text-green-800 text-xs">
                        This dress is free for your selected dates
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <FormItem label="Rental Price" required>
                  <NumberInput
                    name="rental_price"
                    value={rentalPrice}
                    onChange={(value) => setRentalPrice(value || 0)}
                    min={0}
                    step={0.01}
                    prefix="$"
                  />
                </FormItem>

                <FormItem label="Deposit Amount" required>
                  <NumberInput
                    name="deposit_amount"
                    value={depositAmount}
                    onChange={(value) => setDepositAmount(value || 0)}
                    min={0}
                    step={0.01}
                    prefix="$"
                    placeholder="0.00"
                  />
                </FormItem>
              </div>

              <FormItem label="Location (Event Location)" required>
                <Input
                  name="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Where is the dress being rented for?"
                />
              </FormItem>

              <FormItem label="Dress Alterations">
                <div className="space-y-2">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="alterations"
                        value="CLIENT"
                        checked={alterations === "CLIENT"}
                        onChange={(e) =>
                          setAlterations(e.target.value as "CLIENT")
                        }
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        CLIENT
                      </span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="alterations"
                        value="DMU"
                        checked={alterations === "DMU"}
                        onChange={(e) =>
                          setAlterations(e.target.value as "DMU")
                        }
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        DMU
                      </span>
                    </label>
                  </div>
                </div>
              </FormItem>

              <FormItem name="notes" label="Notes">
                <Input placeholder="Additional notes..." />
              </FormItem>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={handleModalClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                {editingRental ? "Update Rental" : "Create Rental"}
              </Button>
            </div>
          </Form>
        )}
      </Modal>

      {/* Rental Contract Modal */}
      {selectedRental && (
        <RentalContract
          rental={selectedRental}
          isOpen={showContract}
          onClose={handleCloseContract}
          onSaveSignatures={handleSaveSignatures}
        />
      )}
    </div>
  );
};

export default RentalManagement;
